import React, { useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerFotoPrincipal } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { iconoCanal } from '../utils/canales';
import { fechaLocal } from '../utils/fecha';
import { formatoCantidad, esPorPeso, unidadDe } from '../utils/unidades';

// Pestaña "Productos vendidos" del Balance: cada producto que salió del
// inventario en el período, con su foto, quién lo compró, cómo se pagó y
// cuánto queda. Tocar uno abre la venta para revisarla o corregirla.

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const sinTildes = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function tituloDia(ymd) {
  const hoy = fechaLocal();
  const ayer = fechaLocal(new Date(Date.now() - 86400000));
  const [a, m, d] = ymd.split('-').map(Number);
  const fecha = new Date(a, m - 1, d);
  const largo = `${DIAS[fecha.getDay()]} ${d} ${MESES[m - 1]}`;
  if (ymd === hoy) return `Hoy · ${largo}`;
  if (ymd === ayer) return `Ayer · ${largo}`;
  return largo.charAt(0).toUpperCase() + largo.slice(1) + (a !== new Date().getFullYear() ? ` ${a}` : '');
}

const hora = (iso) => new Date(iso).toLocaleTimeString('es-HN', { hour: 'numeric', minute: '2-digit' });

// Estado del cobro: pagada, fiada con saldo, o fiada ya pagada.
function estadoCobro(r) {
  if (!r.a_credito) return { texto: '✅ Pagada', fondo: '#DCFCE7', color: '#166534' };
  if (r.cxc && r.cxc.estado === 'pagada') return { texto: '✅ Fiado · ya pagó', fondo: '#DCFCE7', color: '#166534' };
  const debe = r.cxc ? Number(r.cxc.monto_total || 0) - Number(r.cxc.monto_pagado || 0) : null;
  return { texto: debe != null ? `🧾 Fiado · debe ${fmt(debe)}` : '🧾 Fiado', fondo: '#FEF3C7', color: '#92400E' };
}

const chip = (fondo, color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, backgroundColor: fondo, color, whiteSpace: 'nowrap' });

export default function ProductosVendidos({ renglones, productos, onAbrir }) {
  const [busqueda, setBusqueda] = useState('');
  const porId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);

  const filtrados = useMemo(() => {
    const t = sinTildes(busqueda.trim());
    if (!t) return renglones;
    return renglones.filter((r) => [r.nombre_producto, r.codigo_producto, r.cliente?.nombre, r.canal, r.nota, r.concepto]
      .some((x) => sinTildes(x).includes(t)));
  }, [renglones, busqueda]);

  const resumen = useMemo(() => {
    const total = filtrados.reduce((s, r) => s + Number(r.total || 0), 0);
    const conCosto = filtrados.filter((r) => Number(r.costo_unitario) > 0);
    const ganancia = conCosto.reduce((s, r) => s + Number(r.total || 0) - Number(r.costo_unitario) * Number(r.cantidad || 0), 0);
    const fiadoPendiente = filtrados.filter((r) => r.a_credito && r.cxc?.estado !== 'pagada').length;
    return { total, ganancia, hayCosto: conCosto.length > 0, fiadoPendiente };
  }, [filtrados]);

  const grupos = useMemo(() => {
    const mapa = new Map();
    filtrados.forEach((r) => {
      const dia = fechaLocal(new Date(r.vendido_en));
      if (!mapa.has(dia)) mapa.set(dia, []);
      mapa.get(dia).push(r);
    });
    return [...mapa.entries()];
  }, [filtrados]);

  if (renglones.length === 0) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No se vendieron productos en este período.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} aria-label="Buscar en productos vendidos"
          placeholder="🔍 Buscar producto, código o clienta"
          style={{ flex: '1 1 240px', minWidth: 0, padding: '9px 12px', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '14px' }} />
        <span style={chip('#F3F4F6', '#374151')}>{filtrados.length} producto(s)</span>
        <span style={chip('#FCE7F3', '#9D174D')}>Vendido {fmt(resumen.total)}</span>
        {resumen.hayCosto && <span style={chip('#DCFCE7', '#166534')}>Ganancia {fmt(resumen.ganancia)}</span>}
        {resumen.fiadoPendiente > 0 && <span style={chip('#FEF3C7', '#92400E')}>{resumen.fiadoPendiente} fiado(s) sin pagar</span>}
      </div>

      {filtrados.length === 0 && (
        <div style={{ textAlign: 'center', padding: '30px', color: '#9CA3AF' }}>Nada coincide con "{busqueda}".</div>
      )}

      {grupos.map(([dia, filas]) => (
        <div key={dia} style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0 4px 6px' }}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#374151' }}>{tituloDia(dia)}</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
              {filas.length} producto(s) · <strong>{fmt(filas.reduce((s, r) => s + Number(r.total || 0), 0))}</strong>
            </p>
          </div>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #F3F4F6', overflow: 'hidden' }}>
            {filas.map((r) => {
              const prod = porId.get(r.producto_id);
              const unidad = prod ? unidadDe(prod) : 'unidad';
              const foto = prod ? obtenerFotoPrincipal(prod) : null;
              const est = estadoCobro(r);
              const cant = Number(r.cantidad || 0);
              const ganancia = Number(r.costo_unitario) > 0 ? Number(r.total || 0) - Number(r.costo_unitario) * cant : null;
              const stock = prod ? Number(prod.stock || 0) : null;
              return (
                <button key={r.id} type="button" onClick={() => onAbrir(r)} title="Toca para ver o corregir esta venta"
                  style={{ display: 'flex', gap: '12px', width: '100%', textAlign: 'left', padding: '12px', border: 'none', borderTop: '1px solid #F3F4F6', background: 'white', cursor: 'pointer', alignItems: 'flex-start' }}>
                  <div style={{ width: '60px', height: '60px', flexShrink: 0, borderRadius: '10px', overflow: 'hidden', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                    {foto ? <img src={optimizarFoto(foto, 160)} alt={r.nombre_producto} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📦'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'baseline' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.nombre_producto || 'Producto'}</p>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--color-primario,#DB2777)', whiteSpace: 'nowrap' }}>{fmt(r.total)}</p>
                    </div>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                      {r.codigo_producto ? `${r.codigo_producto} · ` : ''}{formatoCantidad(cant, unidad)}{esPorPeso(unidad) ? '' : ' u.'} × {fmt(r.precio_unitario)}
                      {ganancia != null && <span style={{ color: '#166534', fontWeight: 600 }}> · ganancia {fmt(ganancia)}</span>}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                      👤 {r.cliente?.nombre || <span style={{ color: '#9CA3AF', fontWeight: 400 }}>Sin clienta registrada</span>}
                      {r.cliente?.telefono && <span style={{ color: '#6B7280', fontWeight: 400 }}> · {r.cliente.telefono}</span>}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                      <span style={chip(est.fondo, est.color)}>{est.texto}</span>
                      {!r.a_credito && r.metodo_pago && <span style={chip('#F3F4F6', '#374151')}>{r.metodo_pago}</span>}
                      {r.canal && <span style={chip('#F3F4F6', '#374151')}>{iconoCanal(r.canal)} {r.canal}</span>}
                      <span style={chip(stock == null ? '#F3F4F6' : stock > 0 ? '#EFF6FF' : '#FEE2E2', stock == null ? '#6B7280' : stock > 0 ? '#1E40AF' : '#991B1B')}>
                        {stock == null ? 'Producto eliminado' : stock > 0 ? `Quedan ${formatoCantidad(stock, unidad)}` : 'Agotado'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>🕒 {hora(r.vendido_en)}</span>
                    </div>
                    {(r.nota || r.concepto) && (
                      <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>📝 {[r.concepto, r.nota].filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
        Toca un producto para ver la venta completa o corregirla. Aquí salen las ventas pagadas y las fiadas; los envíos no son productos y no aparecen.
      </p>
    </div>
  );
}
